import { useState, useCallback, useEffect } from "react";
import { useSwipeable } from "react-swipeable";
import { useDeviceScreenWidth } from "../../../../hooks/use-device-screen-height";
import { useOpaqueValue } from "../../../../hooks/use-opaque-value";
import { BREAKPOINTS } from "../../../../styles/breakpoints";

export function useNavigation<Item>(items: Array<Item>, ref: React.RefObject<HTMLDivElement | null>) {
  const [visibleIdx, setVisibleIdx] = useState(0);

  const windowWidth = useDeviceScreenWidth();
  const windowWidthRef = useOpaqueValue(windowWidth);

  const shouldPassThroughWalls = useCallback(() => {
    const w = windowWidthRef.current;

    if (typeof w !== "number") {
      return false;
    }

    return w <= BREAKPOINTS.atLeastMd;
  }, []);

  const onPrevious = () => {
    const passThroughWalls = shouldPassThroughWalls();
    setVisibleIdx(s => (s === 0 ? (passThroughWalls ? items.length - 1 : s) : s - 1));
  };

  const onNext = () => {
    const passThroughWalls = shouldPassThroughWalls();
    setVisibleIdx(s => (s === items.length - 1 ? (passThroughWalls ? 0 : s) : s + 1));
  };

  useEffect(() => {
    const { current } = ref;

    if (!current) {
      return;
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "ArrowLeft") {
        onPrevious();
      } else if (event.key === "ArrowRight") {
        onNext();
      }
    }

    current.addEventListener("keydown", onKeyDown);

    return () => {
      current.removeEventListener("keydown", onKeyDown);
    };
  }, [ref]);

  const handlers = useSwipeable({
    onSwipedLeft: () => onNext(),
    onSwipedRight: () => onPrevious(),
    trackMouse: true,
    trackTouch: true,
    touchEventOptions: { passive: true },
  });

  return { handlers, onPrevious, onNext, visibleIdx, setVisibleIdx, shouldPassThroughWalls };
}
